import { computed, ref } from 'vue';

import { defineStore } from 'pinia';

import { acceptedInviteCodes, getStoredInviteCode } from '@/helpers/inviteCode';
import { session } from '@/services/auth/session';
import { useAccountStore } from '@/stores/account';
import { useInvitesStore } from '@/stores/invites';

export const useReceivedInvitesStore = defineStore('receivedInvites', () => {
  const invitesApi = useInvitesStore();
  const accountStore = useAccountStore();

  // Rows currently being accepted/rejected — drives the button loading state.
  const processingCodes = ref([]);

  // Source of truth = GET /users/me → session.user.invites (spread onto the user object in session.js).
  // A stale /users/me can still list an invite the user just accepted (it stays SENT until the
  // next fetch), so hide rows for a code accepted in this tab or still stored for acceptance, and
  // rows for a business profile the user already belongs to. /users/me carries the profile NAME
  // only (no id), so membership is matched by id when present, else by exact name.
  const invites = computed(() => {
    const storedCode = getStoredInviteCode();
    const owned = accountStore.businessProfiles ?? [];
    return (session.user.value?.invites ?? []).filter((invite) => {
      if (acceptedInviteCodes.value.includes(invite.code) || invite.code === storedCode) return false;
      const bpId = invite.businessProfileId;
      const bpName = invite.businessProfileName?.trim();
      return !owned.some((bp) => (bpId != null ? bp.id === bpId : !!bpName && bp.name?.trim() === bpName));
    });
  });

  const hasInvites = computed(() => invites.value.length > 0);
  const count = computed(() => invites.value.length);
  const isProcessing = (code) => processingCodes.value.includes(code);

  // /users/me is the single source of truth; re-fetch so the actioned invite drops off the list.
  const refresh = () => session.restore();

  const runAction = async (invite, apiCall) => {
    if (isProcessing(invite.code)) return;
    processingCodes.value.push(invite.code);
    try {
      await apiCall();
      await refresh();
    } finally {
      processingCodes.value = processingCodes.value.filter((c) => c !== invite.code);
    }
  };

  const accept = (invite) => runAction(invite, () => invitesApi.acceptInvite({ code: invite.code }));

  const reject = (invite) =>
    runAction(invite, () => invitesApi.rejectInvite({ code: invite.code, email: invite.email }));

  return { invites, hasInvites, count, isProcessing, accept, reject, refresh };
});
